<template>
  <div class="app-datatable position-relative">
    <!-- Loading overlay -->
    <vue-element-loading :active="loading" spinner="bar-fade-scale" />

    <!-- Toolbar -->
    <div class="datatable-toolbar d-flex flex-wrap align-items-center gap-2 mb-2" style="min-height:34px;">
      <!-- Lado esquerdo: botões customizados + atualizar -->
      <div class="d-flex gap-2 align-items-center flex-shrink-0">
        <slot name="toolbar" />
        <button v-if="hasRefresh" class="btn btn-sm btn-outline-secondary" @click="$emit('refresh')" title="Atualizar">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          Atualizar
        </button>
      </div>

      <!-- Lado direito: busca, exportar, column chooser -->
      <div class="d-flex gap-2 align-items-center ms-auto flex-shrink-0">
        <!-- Busca -->
        <!-- Em modo servidor a busca local não filtra nada (a página pai tem o próprio filtro): não mostrar o campo -->
        <input v-if="searchEnabled && !isServerMode" type="text" class="form-control form-control-sm"
          style="min-width: 180px; max-width: 260px;"
          placeholder="Buscar na tabela..." v-model="searchQuery" />

        <!-- Exportar Excel -->
        <button v-if="exportEnabled" class="btn btn-sm btn-outline-success text-nowrap" @click="exportToExcel"
          title="Exportar Excel">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Excel
        </button>

        <!-- Column Chooser -->
        <div v-if="columnChooser" class="btn-group custom-dropdown flex-shrink-0" role="group" style="white-space:nowrap;">
          <button type="button" class="btn btn-sm btn-outline-secondary dropdown-toggle text-nowrap"
            data-bs-toggle="dropdown" aria-expanded="false"
            data-bs-auto-close="outside">
            Colunas
          </button>
          <ul class="dropdown-menu" style="right:0;left:auto;">
            <li v-for="col in columns.filter(c => c.title)" :key="col.field">
              <label class="d-flex align-items-center gap-2 px-3 py-1" style="cursor: pointer; white-space: nowrap;">
                <input type="checkbox" :checked="!hiddenFields.has(col.field)"
                  @change="toggleColumn(col.field, $event.target.checked)" />
                {{ col.title }}
              </label>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Tabela HTML própria -->
    <div class="app-dt-table-wrap">
      <table class="app-dt-table">
        <thead>
          <tr>
            <th v-if="hasCheckbox" class="app-dt-th app-dt-th--checkbox">
              <input type="checkbox" @change="toggleAllCheckbox($event)" :checked="allChecked" />
            </th>
            <th v-for="col in visibleColumns" :key="col.field"
              :class="['app-dt-th', { 'app-dt-th--sortable': sortable && col.sortable !== false }]"
              :style="colStyle(col)"
              @click="sortable && col.sortable !== false && onHeaderClick(col)">
              <div class="app-dt-th-content">
                <span>{{ col.title }}</span>
                <span v-if="sortable && col.sortable !== false" class="app-dt-sort-icon">
                  <svg v-if="currentSortField === col.field && currentSortDir === 'asc'"
                    xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="18 15 12 9 6 15"></polyline>
                  </svg>
                  <svg v-else-if="currentSortField === col.field && currentSortDir === 'desc'"
                    xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3">
                    <polyline points="18 15 12 9 6 15"></polyline>
                  </svg>
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="displayRows.length === 0">
            <td :colspan="visibleColumns.length + (hasCheckbox ? 1 : 0)" class="app-dt-empty">
              {{ emptyText }}
            </td>
          </tr>
          <template v-for="(row, idx) in displayRows" :key="row.id ?? idx">
            <tr
              :class="resolveRowClass(row)"
              class="app-dt-row"
              @click="$emit('rowClick', row)">
              <td v-if="hasCheckbox" class="app-dt-td app-dt-td--checkbox">
                <input type="checkbox" :checked="!!row._checked" @change.stop="toggleRowCheckbox(row, $event)" @click.stop />
              </td>
              <td v-for="col in visibleColumns" :key="col.field" class="app-dt-td" :style="colStyle(col)">
                <!-- Se o pai definiu um slot com o nome do campo, usa o slot -->
                <slot v-if="tableSlots.includes(col.field)" :name="col.field" :value="row" />
                <!-- Senão renderiza o valor direto -->
                <template v-else>{{ row[col.field] ?? '' }}</template>
              </td>
            </tr>
            <!-- Linha extra opcional após cada row (ex: motivo desclassificação) -->
            <tr v-if="tableSlots.includes('row-extra') && hasRowExtra(row)" class="app-dt-row-extra">
              <slot name="row-extra" :row="row" :col-count="visibleColumns.length + (hasCheckbox ? 1 : 0)" />
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Paginação -->
    <div v-if="totalItems > 0" class="app-dt-pagination">
      <div class="app-dt-pagination__left">
        <span class="app-dt-pagination__info">
          Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
        </span>
        <select class="app-dt-pagesize-select" :value="activePageSize" @change="onPageSizeChange">
          <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }} / página</option>
        </select>
      </div>
      <div class="app-dt-pagination__buttons">
        <button class="app-dt-page-btn" :disabled="activePage <= 1" @click="goPage(1)" title="Primeira">&laquo;</button>
        <button class="app-dt-page-btn" :disabled="activePage <= 1" @click="goPage(activePage - 1)" title="Anterior">&lsaquo;</button>
        <template v-for="p in visiblePages" :key="p">
          <span v-if="p === '...'" class="app-dt-page-ellipsis">&hellip;</span>
          <button v-else :class="['app-dt-page-btn', { active: p === activePage }]" @click="goPage(p)">{{ p }}</button>
        </template>
        <button class="app-dt-page-btn" :disabled="activePage >= lastPage" @click="goPage(activePage + 1)" title="Próxima">&rsaquo;</button>
        <button class="app-dt-page-btn" :disabled="activePage >= lastPage" @click="goPage(lastPage)" title="Última">&raquo;</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, useSlots, useAttrs, getCurrentInstance } from 'vue';
import VueElementLoading from 'vue-element-loading';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getConfig, gravaConfig } from '@/stores/configsStore';

const props = defineProps({
  // Dados da tabela
  rows: { type: Array, default: () => [] },
  columns: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  totalRows: { type: Number, default: null },

  // Configuração da tabela
  isServerMode: { type: Boolean, default: false },
  pageSize: { type: Number, default: 20 },
  pageSizeOptions: { type: Array, default: () => [10, 20, 50, 100] },
  page: { type: Number, default: null },
  sortable: { type: Boolean, default: true },
  sortColumn: { type: String, default: '' },
  sortDirection: { type: String, default: '' },
  hasCheckbox: { type: Boolean, default: false },
  rowClass: { type: [String, Function], default: '' },

  // Toolbar
  searchEnabled: { type: Boolean, default: true },
  exportEnabled: { type: Boolean, default: false },
  exportFilename: { type: String, default: 'export.xlsx' },
  columnChooser: { type: Boolean, default: false },

  // Texto da tabela vazia (padrão de sempre)
  emptyText: { type: String, default: 'Nenhum dado disponível' },

  // Função opcional que indica se uma row tem conteúdo extra (controla visibilidade do <tr> extra)
  rowExtraIf: { type: Function, default: null },

  // Persistência — chave única para salvar config do grid (ex: 'grid.comprasnet', 'grid.busca')
  configKey: { type: String, default: '' },
  // Categoria para salvar com tipo no banco (ex: 'pregoes'). Se vazio, usa flag 'json' legada.
  configTipo: { type: String, default: '' },
  // Config pré-carregada pelo pai — evita request próprio quando fornecida
  preloadedConfig: { type: Object, default: null },
});

const emit = defineEmits(['refresh', 'rowClick', 'change', 'pageChange', 'pageSizeChange']);

const slotsObj = useSlots();
const attrs = useAttrs();
const instance = getCurrentInstance();
const searchQuery = ref('');
const internalPage = ref(1);
const activePageSize = ref(props.pageSize);

// ── Colunas ocultas (state interno — não muta a prop columns) ──
const hiddenFields = ref(new Set());
const colConfigLoaded = ref(false);

// Inicializa hiddenFields a partir de col.hide (antes da config carregar)
watch(() => props.columns, (cols) => {
  if (colConfigLoaded.value) return;
  const initial = new Set();
  cols.forEach(col => { if (col.hide) initial.add(col.field); });
  hiddenFields.value = initial;
}, { immediate: true });

const toggleColumn = (field, visible) => {
  const next = new Set(hiddenFields.value);
  if (visible) next.delete(field);
  else next.add(field);
  hiddenFields.value = next;
  saveGridConfig();
};

// Ordenação interna (client mode)
const currentSortField = ref(props.sortColumn || '');
const currentSortDir = ref(props.sortDirection || '');

// Sincroniza pageSize com a prop
watch(() => props.pageSize, (v) => { activePageSize.value = v; });

// ── Persistência de config do grid ──
const configLoaded = ref(false);

const saveGridConfig = () => {
  if (!props.configKey) return;
  const cfg = JSON.stringify({
    pageSize: activePageSize.value,
    hiddenFields: [...hiddenFields.value],
  });
  // Usa configTipo como categoria se informado; senão usa flag legada 'json'
  const tipo = props.configTipo || 'json';
  gravaConfig(tipo, { chave: props.configKey, valor: cfg }).catch(() => { });
};

const _aplicarConfigGrid = (raw) => {
  if (!raw) return;
  try {
    const cfg = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (cfg.pageSize && props.pageSizeOptions.includes(cfg.pageSize)) {
      activePageSize.value = cfg.pageSize;
      emit('pageSizeChange', cfg.pageSize);
      if (props.isServerMode) {
        emit('change', { current_page: 1, pagesize: cfg.pageSize, offset: 0, change_type: 'pageSize' });
      }
    }
    if (cfg.hiddenFields && Array.isArray(cfg.hiddenFields)) {
      hiddenFields.value = new Set(cfg.hiddenFields);
      colConfigLoaded.value = true;
    }
  } catch { /* ignora JSON inválido */ }
};

onMounted(() => {
  if (!props.configKey) { configLoaded.value = true; return; }

  // Se o pai pré-carregou a config (ex: via GetConfigsByTipo), aplica direto sem request
  if (props.preloadedConfig) {
    const raw = props.preloadedConfig.json || props.preloadedConfig.valor;
    _aplicarConfigGrid(raw);
    configLoaded.value = true;
    return;
  }

  getConfig(props.configKey).then(res => {
    const raw = res?.data?.json || res?.data?.valor;
    _aplicarConfigGrid(raw);
  }).catch(() => { }).finally(() => { configLoaded.value = true; });
});

const onPageSizeChange = (e) => {
  const newSize = Number(e.target.value);
  activePageSize.value = newSize;
  internalPage.value = 1;
  emit('pageSizeChange', newSize);
  if (props.isServerMode) {
    emit('change', {
      current_page: 1,
      pagesize: newSize,
      offset: 0,
      change_type: 'pageSize',
    });
  }
  saveGridConfig();
};

// Página ativa
const activePage = computed(() => {
  if (props.isServerMode && props.page != null) return props.page;
  return internalPage.value;
});

// Sincroniza página interna com a prop do pai
watch(() => props.page, (newPage) => {
  if (newPage != null && newPage !== internalPage.value) {
    internalPage.value = newPage;
  }
});

// Reseta página 1 quando dados mudam (client mode)
watch(() => props.rows, () => {
  if (!props.isServerMode) {
    internalPage.value = 1;
  }
});

// Reseta para página 1 ao buscar
watch(searchQuery, () => { internalPage.value = 1; });

// Colunas visíveis — usa hiddenFields (state interno) para não mutar a prop
const visibleColumns = computed(() => props.columns.filter(c => !hiddenFields.value.has(c.field) && c.field));

// Estilo da coluna
const colStyle = (col) => {
  const s = {};
  if (col.width) s.width = typeof col.width === 'number' ? col.width + 'px' : col.width;
  if (col.minWidth) s.minWidth = typeof col.minWidth === 'number' ? col.minWidth + 'px' : col.minWidth;
  if (col.maxWidth) s.maxWidth = typeof col.maxWidth === 'number' ? col.maxWidth + 'px' : col.maxWidth;
  return s;
};

// Resolver classe da row
const resolveRowClass = (row) => {
  if (!props.rowClass) return '';
  if (typeof props.rowClass === 'function') return props.rowClass(row);
  return props.rowClass;
};

// ── Filtro de busca (client mode) ──
const filteredRows = computed(() => {
  const rows = props.rows ?? [];
  if (props.isServerMode || !searchQuery.value) return rows;
  const q = searchQuery.value.toLowerCase();
  return rows.filter(row =>
    Object.values(row).some(val => val != null && String(val).toLowerCase().includes(q))
  );
});

// ── Ordenação client-side ──
const sortedRows = computed(() => {
  const rows = filteredRows.value;
  // Em server mode, nunca reordena — respeita a ordem do backend
  if (props.isServerMode) return rows;
  // Se não há coluna de ordenação, retorna na ordem original
  if (!currentSortField.value || !currentSortDir.value) return rows;

  const field = currentSortField.value;
  const dir = currentSortDir.value === 'desc' ? -1 : 1;

  return [...rows].sort((a, b) => {
    let va = a[field];
    let vb = b[field];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    // Tenta comparar como número
    const na = Number(va);
    const nb = Number(vb);
    if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
    // Senão compara como string
    return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true }) * dir;
  });
});

// ── Paginação ──
const totalItems = computed(() => {
  if (props.isServerMode) return Number(props.totalRows) || 0;
  return sortedRows.value.length;
});

const lastPage = computed(() => Math.max(1, Math.ceil(totalItems.value / activePageSize.value)));

const startRow = computed(() => {
  const total = totalItems.value;
  const page = Number(activePage.value) || 1;
  const size = Number(activePageSize.value) || 20;
  return total === 0 ? 0 : (page - 1) * size + 1;
});
const endRow = computed(() => {
  const total = totalItems.value;
  const page = Number(activePage.value) || 1;
  const size = Number(activePageSize.value) || 20;
  return Math.min(page * size, total);
});

// Rows da página atual
const paginatedRows = computed(() => {
  if (props.isServerMode) return props.rows ?? [];
  const start = (activePage.value - 1) * activePageSize.value;
  return sortedRows.value.slice(start, start + activePageSize.value);
});

// Rows finais exibidas
const displayRows = computed(() => paginatedRows.value);

// Páginas visíveis com elipses
const visiblePages = computed(() => {
  const total = lastPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const cur = activePage.value;
  const pages = [];
  pages.push(1);
  if (cur > 3) pages.push('...');
  for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) pages.push(p);
  if (cur < total - 2) pages.push('...');
  pages.push(total);
  return pages;
});

const goPage = (p) => {
  if (p < 1 || p > lastPage.value || p === activePage.value) return;
  internalPage.value = p;
  if (props.isServerMode) {
    emit('change', {
      current_page: p,
      pagesize: activePageSize.value,
      offset: (p - 1) * activePageSize.value,
      change_type: 'page',
    });
  }
  emit('pageChange', p);
};

// ── Ordenação por clique no cabeçalho ──
const onHeaderClick = (col) => {
  if (currentSortField.value === col.field) {
    // Cicla: asc → desc → sem ordenação
    if (currentSortDir.value === 'asc') {
      currentSortDir.value = 'desc';
    } else {
      currentSortField.value = '';
      currentSortDir.value = '';
    }
  } else {
    currentSortField.value = col.field;
    currentSortDir.value = 'asc';
  }

  if (props.isServerMode) {
    emit('change', {
      current_page: activePage.value,
      pagesize: activePageSize.value,
      offset: (activePage.value - 1) * activePageSize.value,
      sort_column: currentSortField.value,
      sort_direction: currentSortDir.value,
      change_type: 'sort',
    });
  }
};

// ── Checkbox ──
const allChecked = computed(() => {
  const rows = displayRows.value;
  return rows.length > 0 && rows.every(r => r._checked);
});

const toggleAllCheckbox = (e) => {
  const val = e.target.checked;
  displayRows.value.forEach(r => { r._checked = val; });
};

const toggleRowCheckbox = (row, e) => {
  row._checked = e.target.checked;
};

// Determina se uma row tem conteúdo extra — usa prop rowExtraIf se fornecida, senão sempre true
const hasRowExtra = (row) => {
  if (props.rowExtraIf) return props.rowExtraIf(row);
  return true;
};

// Mostra o botão Atualizar quando há um listener @refresh
// Usa vnode.props pois defineEmits remove o handler de attrs
const hasRefresh = computed(() => !!instance?.vnode?.props?.onRefresh);

// Slots de células (excluindo o slot 'toolbar')
const tableSlots = computed(() =>
  Object.keys(slotsObj).filter(name => name !== 'toolbar')
);

// Exporta os dados visíveis para Excel
const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Dados');

  // coluna com export: false (ex.: botões de ação) não vai para o Excel
  const visibleCols = props.columns.filter(c => !c.hide && c.export !== false);
  worksheet.addRow(visibleCols.map(c => c.title));

  props.rows.forEach(row => {
    // coluna pode definir exportValue(row) quando o valor exibido difere do campo bruto (ex.: percentual de desconto)
    worksheet.addRow(visibleCols.map(c => (c.exportValue ? c.exportValue(row) : row[c.field]) ?? ''));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), props.exportFilename);
};
</script>

<style scoped>
.app-datatable {
  min-height: 60px;
  min-width: 0;
  width: 100%;
}

.datatable-toolbar {
  min-width: 0;
}
.datatable-toolbar > div {
  min-width: 0;
}
/* Celular (Task 25): os dois blocos da toolbar (botões do slot + Atualizar | busca, Excel, Colunas) quebram linha
   em vez de alargar a PÁGINA (a tabela já rola por dentro). !important: os blocos usam a classe flex-shrink-0 do
   Bootstrap. Só abaixo de 768 px: no desktop a toolbar fica exatamente como sempre foi. */
@media (max-width: 767.98px) {
  .datatable-toolbar > div {
    flex-wrap: wrap;
    flex-shrink: 1 !important;
    max-width: 100%;
  }
}

/* ── Tabela ── */
.app-dt-table-wrap {
  overflow-x: auto;
}
.app-dt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.app-dt-th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: #344054;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  white-space: nowrap;
  user-select: none;
}
.app-dt-th--sortable {
  cursor: pointer;
}
.app-dt-th--sortable:hover {
  background: #e9ecef;
}
.app-dt-th-content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.app-dt-sort-icon {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
}
.app-dt-td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #344054;
  vertical-align: middle;
  background: #fff;
}
.app-dt-td--checkbox,
.app-dt-th--checkbox {
  width: 40px;
  text-align: center;
}
.app-dt-row {
  transition: background 0.1s;
  cursor: pointer;
}
.app-dt-row:hover {
  background: #f0f4ff;
}
.app-dt-row:hover .app-dt-td {
  background: #f0f4ff;
}
.app-dt-empty {
  padding: 32px 12px;
  text-align: center;
  color: #999;
  font-style: italic;
}

/* ── Paginação ── */
.app-dt-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 4px 4px;
}
.app-dt-pagination__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.app-dt-pagination__info {
  font-size: 0.82rem;
  color: #888;
}
.app-dt-pagesize-select {
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font-size: 0.8rem;
  padding: 4px 8px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
}
.app-dt-pagesize-select:hover,
.app-dt-pagesize-select:focus {
  border-color: #4361ee;
}
.app-dt-pagination__buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}
.app-dt-page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
}
.app-dt-page-btn:hover:not(:disabled):not(.active) {
  border-color: #4361ee;
  background: #4361ee;
  color: #fff;
}
.app-dt-page-btn.active {
  border-color: #4361ee;
  background: #4361ee;
  color: #fff;
  font-weight: 600;
}
.app-dt-page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.app-dt-page-ellipsis {
  padding: 0 4px;
  color: #999;
  font-size: 0.85rem;
}
</style>

<style>
/* ── Dark mode ──
   Bloco SEM escopo de propósito: no Vue 3.2 o `:global(.dark) .classe` dentro de <style scoped>
   não é compilado (as regras escuras nunca chegavam ao CSS). `body.dark` vence as regras claras
   (.classe[data-v]) por especificidade, independente da ordem. */
body.dark .app-dt-th {
  background: #1a2036;
  border-color: #253159;
  color: #e0e6ed;
}
body.dark .app-dt-th--sortable:hover {
  background: #253159;
}
body.dark .app-dt-td {
  border-color: #253159;
  color: #e0e6ed;
  background: #0e1726;
}
body.dark .app-dt-row:hover {
  background: #1e2a4a;
}
body.dark .app-dt-row:hover .app-dt-td {
  background: #1e2a4a;
}
body.dark .app-dt-empty {
  color: #666;
}
body.dark .app-dt-pagination__info {
  color: #888ea8;
}
body.dark .app-dt-pagesize-select {
  background: #1a2036;
  border-color: #253159;
  color: #e0e6ed;
}
body.dark .app-dt-page-btn {
  background: #1a2036;
  border-color: #253159;
  color: #e0e6ed;
}
body.dark .app-dt-page-btn:hover:not(:disabled):not(.active) {
  border-color: #4361ee;
  background: #4361ee;
  color: #fff;
}
body.dark .app-dt-page-btn.active {
  border-color: #4361ee;
  background: #4361ee;
  color: #fff;
}
</style>
